// Helpers compartidos para trabajar con la jerarquía WBS y el avance agregado
// de las actividades de Programa. Usados por MatrizPrograma y GanttPrograma
// para mantener exactamente la misma lógica de agrupación/avance en ambas vistas.

export const ESTADO_LABEL = {
  pendiente: 'Pendiente',
  en_progreso: 'En progreso',
  completado: 'Completado',
  atrasado: 'Atrasado'
};

const MS_POR_DIA = 1000 * 60 * 60 * 24;

// Pre-computa metadata jerárquica para las actividades de una sección
export function computarMetaActividades(actividades) {
  // Solo los wbsCodes que existen como actividades reales
  const wbsSet = new Set(actividades.map(a => a.wbsCode).filter(Boolean));

  // Un código es "padre" si algún ancestro suyo (del propio set) tiene hijos
  // → recorremos cada código y marcamos sus ancestros REALES como padres
  const parentCodes = new Set();
  for (const code of wbsSet) {
    const parts = code.split('.');
    for (let i = 1; i < parts.length; i++) {
      const ancestor = parts.slice(0, i).join('.');
      if (wbsSet.has(ancestor)) {
        parentCodes.add(ancestor);
      }
    }
  }

  const depths = actividades.map(a => a.wbsCode ? a.wbsCode.split('.').length : 2);
  const minDepth = depths.length > 0 ? Math.min(...depths) : 2;

  return {
    parentCodes,
    actividades: actividades.map(a => ({
      ...a,
      esParent: a.wbsCode ? parentCodes.has(a.wbsCode) : false,
      relDepth: (a.wbsCode ? a.wbsCode.split('.').length : minDepth) - minDepth
    }))
  };
}

// Calcula el avance agregado de cada actividad de abajo hacia arriba (ponderado por duración).
// Las hojas usan su porcentajeAvance real; los padres calculan el promedio ponderado de sus hijos directos.
export function calcularAvancesAgregados(actividades) {
  // Mapa de hijos directos: wbsCode → [hijos]
  const childrenMap = {};
  for (const act of actividades) {
    if (!act.wbsCode) continue;
    const parts = act.wbsCode.split('.');
    if (parts.length > 1) {
      const parentCode = parts.slice(0, -1).join('.');
      if (!childrenMap[parentCode]) childrenMap[parentCode] = [];
      childrenMap[parentCode].push(act);
    }
  }

  const avanceCalc = {};  // wbsCode → % calculado
  const durCalc = {};     // wbsCode → duración total acumulada de descendientes

  // Procesar de mayor profundidad a menor (hojas primero)
  const sorted = [...actividades].sort(
    (a, b) => (b.wbsCode?.split('.').length || 0) - (a.wbsCode?.split('.').length || 0)
  );

  for (const act of sorted) {
    if (!act.wbsCode) continue;
    const children = childrenMap[act.wbsCode] || [];

    if (children.length === 0) {
      // Hoja: usar valor real de la base de datos
      avanceCalc[act.wbsCode] = act.porcentajeAvance || 0;
      durCalc[act.wbsCode] = act.duracionBase || 1;
    } else {
      // Padre: promedio ponderado por duración de los hijos directos
      let sumDur = 0, sumAvance = 0;
      for (const child of children) {
        const dur = durCalc[child.wbsCode] ?? child.duracionBase ?? 1;
        sumDur += dur;
        sumAvance += (avanceCalc[child.wbsCode] ?? 0) * dur;
      }
      avanceCalc[act.wbsCode] = sumDur > 0 ? Math.round(sumAvance / sumDur) : 0;
      durCalc[act.wbsCode] = sumDur;
    }
  }

  return { avanceCalc, durCalc };
}

export function estadoDesdeAvance(avance) {
  if (avance >= 100) return 'completado';
  if (avance > 0) return 'en_progreso';
  return 'pendiente';
}

// % de avance que debería llevar la actividad a la fecha de hoy, según sus fechas base
export function calcularAvanceEsperado(fechaInicioBase, fechaFinBase, hoy) {
  if (!fechaInicioBase || !fechaFinBase) return 0;
  const ini = new Date(fechaInicioBase);
  const fin = new Date(fechaFinBase);
  const rango = (fin - ini) / MS_POR_DIA || 1;
  const transcurrido = (hoy - ini) / MS_POR_DIA;
  return Math.min(100, Math.max(0, (transcurrido / rango) * 100));
}

// Rojo/naranjo/amarillo/verde según qué tan atrasada va la actividad respecto a lo esperado
export function colorDesdeAvance(avanceReal, avanceEsperado) {
  if (avanceReal >= 100) return '#22c55e';            // verde — completado
  const brecha = avanceEsperado - avanceReal;
  if (brecha > 20) return '#ef4444';                  // rojo — muy atrasado
  if (brecha > 10) return '#f97316';                  // naranjo — atrasado
  return '#eab308';                                   // amarillo — a tiempo / leve atraso
}

// Una actividad es visible si ningún antecesor está colapsado
export function esVisible(act, actividadesColapsadas) {
  if (!act.wbsCode) return true;
  const parts = act.wbsCode.split('.');
  for (let len = 1; len < parts.length; len++) {
    if (actividadesColapsadas.has(parts.slice(0, len).join('.'))) return false;
  }
  return true;
}
